/**
 * Comms reach at L2 (IDEAS X21, the sector side; the clock brief is ruled, 29). Orders and reports travel over the
 * comms net: from the faction's relays — its home, and every held system with an antenna standing — lane by lane,
 * routing round enemy-held systems. Each lane costs `rules.commsDaysPerLane` days. An order to a squad beyond a
 * relay is in flight until it arrives (sector.ts delivers it, then it is validated and applied as if given then);
 * a report from a system beyond a relay sits in the faction's inbox until it arrives; where no route reaches, the
 * order is lost and the report never comes (a cut). The panel says what is in flight and what is cut. The
 * commander's view (knownView) is what has arrived, so the stand-in plans on stale pictures like the player.
 * Behind `rules.commsReach`. Pure.
 */
import type { FactionId, FactionKnowledge, KnownSquad, KnownSystem, Order, SectorState, Squad, SquadId, SystemId } from './types';

export interface OrderInFlight { squadId: SquadId; order: Order; issued: number; arrives: number; lanes: number }
export interface ReportInFlight { arrives: number; system?: KnownSystem; squad?: KnownSquad }

/** The faction's relays: its home, and every held system with an antenna standing. */
export function relaysOf(state: Pick<SectorState, 'factions' | 'systems'>, faction: FactionId): Set<SystemId> {
  const out = new Set<SystemId>();
  const home = state.factions.find(f => f.id === faction)?.home;
  if (home) out.add(home);
  for (const sys of state.systems) if (sys.owner === faction && sys.structures.some(b => b.kind === 'antenna')) out.add(sys.id);
  return out;
}

/**
 * Lanes over the comms net from the nearest relay to `systemId`, routing round systems an enemy holds (only the
 * end may be theirs: a squad standing there, a report of it). Null when no route reaches: a cut.
 */
export function commsLanes(state: Pick<SectorState, 'factions' | 'systems' | 'lanes'>, faction: FactionId, systemId: SystemId): number | null {
  const relays = relaysOf(state, faction);
  if (relays.has(systemId)) return 0;
  const blocked = new Set(state.systems.filter(s => s.owner !== null && s.owner !== faction && s.id !== systemId).map(s => s.id));
  const dist = new Map<SystemId, number>();
  const queue: SystemId[] = [];
  for (const r of relays) { dist.set(r, 0); queue.push(r); }
  while (queue.length) {
    const cur = queue.shift()!;
    const d = dist.get(cur)!;
    for (const l of state.lanes) {
      const next = l.a === cur ? l.b : l.b === cur ? l.a : null;
      if (next === null || dist.has(next)) continue;
      if (next === systemId) return d + 1;
      if (blocked.has(next)) continue;
      dist.set(next, d + 1);
      queue.push(next);
    }
  }
  return null;
}

/** Days for an order or a report to cross between the nearest relay and `systemId`; null when cut. */
export function commsDelay(state: Pick<SectorState, 'factions' | 'systems' | 'lanes' | 'rules'>, faction: FactionId, systemId: SystemId): number | null {
  const lanes = commsLanes(state, faction, systemId);
  return lanes === null ? null : lanes * state.rules.commsDaysPerLane;
}

/** Where a squad is for comms: where it stands, or where it is bound. */
export function squadWhere(q: Pick<Squad, 'at' | 'transit'>): SystemId {
  return q.at ?? q.transit!.to;
}

/** The order in flight to a squad, if any. */
export function orderInFlightFor(state: Pick<SectorState, 'ordersInFlight'>, squadId: SquadId): OrderInFlight | undefined {
  return state.ordersInFlight?.find(o => o.squadId === squadId);
}

/**
 * Where a look's eyes stand, for the report's trip home: a system, or null when the eyes are on the net already (an
 * antenna or sensor on a relay, the satellite's downlink) and the report arrives at once.
 */
export type ReportFrom = (faction: FactionId, about: SystemId) => SystemId | null;

/** The default: the eyes stand where they look (a squad there, a holder's garrison). */
export const FROM_THERE: ReportFrom = (_f, about) => about;

/**
 * Relay what a look just wrote: every report in `next` that is new today travels from where its eyes stood (`from`)
 * to the nearest relay — to the faction's inbox (or lost, when cut), the record keeping what it had. Reports whose
 * eyes stood at a relay arrive at once.
 */
export function relayReports(prev: SectorState, next: SectorState, from: ReportFrom = FROM_THERE): SectorState {
  if (!next.rules.commsReach) return next;
  const day = next.day;
  const knowledge: Record<FactionId, FactionKnowledge> = { ...next.knowledge };
  let changed = false;
  for (const f of next.factions) {
    const was = prev.knowledge[f.id];
    const now = next.knowledge[f.id];
    if (!now) continue;
    const systems = { ...now.systems };
    const squads = { ...now.squads };
    const inbox = [...(now.inbox ?? [])];
    const queue = (arrives: number | null, entry: Omit<ReportInFlight, 'arrives'>, key: (r: ReportInFlight) => boolean) => {
      if (arrives === null) return;   // cut: the report never comes
      const i = inbox.findIndex(key);
      if (i >= 0) inbox.splice(i, 1);
      inbox.push({ arrives, ...entry });
    };
    for (const [id, r] of Object.entries(now.systems)) {
      const old = was?.systems[id];
      if (r.seenDay < day - 1e-9 || (old && old.seenDay >= r.seenDay) || r.via) continue;   // not new today (a stolen picture came with the ground)
      const eyes = from(f.id, id);
      const delay = eyes === null ? 0 : commsDelay(next, f.id, eyes);
      if (delay === 0) continue;
      changed = true;
      if (old) systems[id] = old; else delete systems[id];
      queue(delay === null ? null : day + delay, { system: r }, x => x.system?.systemId === id);
    }
    for (const [id, r] of Object.entries(now.squads)) {
      const old = was?.squads[id];
      if (r.seenDay < day - 1e-9 || (old && old.seenDay >= r.seenDay) || r.via) continue;
      const where = r.at ?? r.transitTo;
      const eyes = where ? from(f.id, where) : null;
      const delay = eyes === null ? 0 : commsDelay(next, f.id, eyes);
      if (delay === 0) continue;
      changed = true;
      if (old) squads[id] = old; else delete squads[id];
      queue(delay === null ? null : day + delay, { squad: r }, x => x.squad?.squadId === id);
    }
    knowledge[f.id] = { ...now, systems, squads, ...(inbox.length ? { inbox } : {}) };
  }
  return changed ? { ...next, knowledge } : next;
}

/** Deliver every report whose day has come (fresher than what the record holds). */
export function deliverReports(state: SectorState): SectorState {
  if (!state.rules.commsReach) return state;
  const knowledge: Record<FactionId, FactionKnowledge> = { ...state.knowledge };
  let changed = false;
  for (const [fid, k] of Object.entries(state.knowledge)) {
    if (!k.inbox?.length) continue;
    const due = k.inbox.filter(r => r.arrives <= state.day + 1e-9);
    if (!due.length) continue;
    changed = true;
    const systems = { ...k.systems };
    const squads = { ...k.squads };
    for (const r of due) {
      if (r.system && (!systems[r.system.systemId] || systems[r.system.systemId].seenDay <= r.system.seenDay)) systems[r.system.systemId] = r.system;
      if (r.squad && (!squads[r.squad.squadId] || squads[r.squad.squadId].seenDay <= r.squad.seenDay)) squads[r.squad.squadId] = r.squad;
    }
    const left = k.inbox.filter(r => r.arrives > state.day + 1e-9);
    knowledge[fid] = { ...k, systems, squads, ...(left.length ? { inbox: left } : { inbox: undefined }) };
  }
  return changed ? { ...state, knowledge } : state;
}

/** The report of a system in flight to a faction, if any. */
export function reportInFlightFor(state: Pick<SectorState, 'knowledge'>, faction: FactionId, systemId: SystemId): ReportInFlight | undefined {
  return state.knowledge[faction]?.inbox?.find(r => r.system?.systemId === systemId);
}

/** One line for the panel: how far a system is over the comms net. */
export function commsText(state: Pick<SectorState, 'factions' | 'systems' | 'lanes' | 'rules'>, faction: FactionId, systemId: SystemId): string {
  const lanes = commsLanes(state, faction, systemId);
  if (lanes === null) return 'cut — no relay reaches here; orders are lost, reports never come';
  if (lanes === 0) return 'a relay — orders and reports at once';
  const days = lanes * state.rules.commsDaysPerLane;
  return `${lanes} lane${lanes === 1 ? '' : 's'} from a relay — orders and reports take ${days} day${days === 1 ? '' : 's'}`;
}
